// Upload templates (.xlsx) for the "Cargar pagos" flows. Built with exceljs because
// SheetJS community does not write data validations (the dropdowns) or column formats.
// exceljs is loaded on demand so it only weighs on the page when a template is downloaded.

/** Rows a template prepares for typing (text format and dropdowns). */
export const TEMPLATE_ROWS = 1000;

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

async function loadExcelJS() {
  const module = await import('exceljs');
  return module.default || module;
}

/**
 * Adds one sheet: bold headers, every column in text format (so Excel keeps leading
 * zeros and dates as typed), a note on headers that need one, and a dropdown on the
 * columns with a closed list of values.
 */
function addSheet(workbook, name, columns, rows) {
  const sheet = workbook.addWorksheet(name, { views: [{ state: 'frozen', ySplit: 1 }] });
  sheet.columns = columns.map((column) => ({ header: column.header, width: column.width, style: { numFmt: '@' } }));
  sheet.getRow(1).font = { bold: true };
  rows.forEach((row) => sheet.addRow(row));

  columns.forEach((column, index) => {
    const letter = sheet.getColumn(index + 1).letter;
    if (column.note) sheet.getCell(`${letter}1`).note = column.note;
    if (column.list) {
      sheet.dataValidations.add(`${letter}2:${letter}${TEMPLATE_ROWS + 1}`, {
        type: 'list',
        allowBlank: true,
        formulae: [`"${column.list.join(',')}"`],
        showInputMessage: true,
        promptTitle: column.header,
        prompt: 'Elige una opción de la lista.',
        showErrorMessage: true,
        errorStyle: 'stop',
        errorTitle: `${column.header} no válido`,
        error: `Elige una de estas opciones: ${column.list.join(' / ')}.`
      });
    }
  });
}

/**
 * Workbook of an upload template: the first sheet has only the headers and is the one
 * the upload reads; the second ("Ejemplo") shows filled rows.
 * @param {{ sheetName: string, columns: Array<{header: string, width: number, note?: string, list?: string[]}>, examples: string[][] }} template
 */
export async function buildTemplateWorkbook({ sheetName, columns, examples }) {
  const ExcelJS = await loadExcelJS();
  const workbook = new ExcelJS.Workbook();
  addSheet(workbook, sheetName, columns, []);
  addSheet(workbook, 'Ejemplo', columns, examples);
  return workbook;
}

/** Saves an exceljs workbook as an .xlsx file through a temporary link. */
export async function downloadWorkbook(workbook, filename) {
  const buffer = await workbook.xlsx.writeBuffer();
  const url = URL.createObjectURL(new Blob([buffer], { type: XLSX_MIME }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
